<template>
  <div class="card">

    <div class="bg-blue-light p-1">
      <TitleButton
        :showBtn="true"
        :showAddNew="false"
        title="Partial Return IPD Bill"
        @onClickCloseButton="goToList"
      />
    </div>

    <div class="my-2 px-2">
      <TableHeaderIPDBillAdd
        :formData="formData"
        :patient="patient"
        :tableItems="tableItems"
        :is-return="true"
      />
    </div>

    <div class="col-12 px-2">
      <ReturnIpdPartialTable
        :tableItems="tableItems"
        :salesAccountHeads="salesAccountHeads"
        :is-hide-add-btn="true"
        :from-return="true"
      />
    </div>
    <div class="row px-2">
      <div class="col-md-6">
        <div class="mb-1">
          <label class="form-label" for="description">Ledger Memo</label>
          <vField
            as="textarea"
            name="description"
            type="number"
            class="form-control"
            v-model="formData.description"
          />
        </div>
      </div>
      <div class="col-md-5 ml-auto mt-auto mb-1">
        <div class="d-flex flex-wrap gap-1 gy-2">
          <button @click="handleSubmit" class="btn btn-primary">Return Bill</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import {computed, inject, onMounted, provide, reactive, ref, watch} from 'vue';
import TitleButton from '@/components/atom/TitleButton';
import {useRoute, useRouter} from 'vue-router';
import {generateUUID} from '@/services/utils/global';
import handlePurchase from '@/services/modules/purchase';
import handleCompany from "@/services/modules/company";
import TableHeaderIPDBillAdd
  from "@/components/molecule/company/hospital/billing/ipd-bill/TableHeaderHospitalBillAdd.vue";
import handleHospital from "@/services/modules/hospital";
import useDate from "@/services/utils/day";
import handleHospitalBilling from "@/services/modules/hospital/billing";
import salesReturnHelper from "@/services/utils/pdf/salesReturnHelper";
import ReturnIpdPartialTable
  from "@/components/molecule/company/hospital/billing/bill-manager/ReturnIpdPartialTable.vue";

const {fetchSalesBill, ...rest} = handlePurchase();
const {fetchCompanyInfo} = handleCompany();
const {fetchSingleNote} = handleHospital();
const {returnBillings} = handleHospitalBilling();
const {fetchAndGeneratePdf} = salesReturnHelper();

const $route = useRoute();
const $router = useRouter();
const dateHelper = useDate();

const companyId = $route.params.companyId;
const loader = ref(true);
const salesAccountHeads = ref([]);
const totals = ref({});
const company = ref({});
const patient = ref({});

let formData = ref({
  company_id: companyId,
  contact_profile_id: null,
  account_head_id: null,
  ipd_register_id: null,
  vat_payable_account_head_id: null,
  mop_account_head_id: null,
  receipt_reference: null,
  receipt_description: "IPD bill payment",
  payment: null,
  project_id: null,
  cost_centre_id: null,
  location_id: null,
  discount_head_id: null,
  adjustment_amount: 0,
  status: 'active',
  bill_number: '',
  date: '',
  has_item_detail: true,
  description: '',
  order_number: null,
  sales_person_id: null,
  commission_percent: null,
  lc_number: null,
  item_details: []
});

provide('formData', formData);
provide('totals', totals);
const showSuccess = inject('showSuccess');
const showError = inject('showError');

const ipdRegisterId = computed(() => formData.value.ipd_register_id);

watch(ipdRegisterId, (newValue) => {
  if (!newValue) {
    return;
  }
  const companyQuery = `?company_id=${companyId}`;
  fetchSingleNote(companyQuery, newValue).then(res => {
    if (!res.data) {
      return;
    }
    patient.value = res.data;
    formData.value.contact_profile_id = res.data.patient.id;
    formData.value.ipd_register_id = res.data.id;
    formData.value.service_resource_id = res.data.human_resource_id;
  });
});

let tableItems = reactive([]);

const formatGeneralItems = (generalItems) => {
  return generalItems.map(td => {
    return {
      id: td.id,
      product_id: td.product_id,
      name: td.product.name,
      option_name: td.product.name,
      description: td.description,
      quantity: td.quantity,
      prev_return_qty: td.prev_return_qty,
      rate: td.rate,
      discount: td.discount,
      sale_general_id: td.id,
      vat: td.vat,
      sub_total: td.sub_total,
      discount_amount: td.discount_amount,
      discount_percent: td.discount_percent,
      vat_amount: td.vat_amount,
      total: td.total,
      is_edit: false,
      product: {name: td.product.name},
      amount: td.amount,
      total_amount: td.total_amount
    };
  });
};

onMounted(async () => {
  const companyQuery = `?company_id=${companyId}`;
  const salesBillQuery = `${companyQuery}&return_data=1`;
  const receiveAbleRes = rest.fetchAccountReceivable(companyQuery);
  const vatPayableRes = rest.fetchVatPayable(companyQuery);
  const fetchCompany = fetchCompanyInfo(companyId);
  const salesAccountHeadsRes = rest.getAccountHeadBySlag("sales_accounts", companyQuery);
  const getSalesBill = fetchSalesBill($route.params.invoiceId, salesBillQuery);

  Promise.all([
    receiveAbleRes.then(res => {
      if (res.data) formData.value.account_head_id = res.data[0].id;
    }),
    vatPayableRes.then(res => {
      if (res.data) formData.value.vat_payable_account_head_id = res.data[0].id;
    }),
    fetchCompany.then(res => {
      if (res.data) company.value = res.data;
    }),
    salesAccountHeadsRes.then(res => {
      if (res.data) salesAccountHeads.value = res.data;
    }),
    getSalesBill.then(res => {
      if (!res.data) {
        return;
      }
      const {id, date, discount_ledger, general, receipt_generals} = res.data;
      formData.value = res.data;
      formData.value.sale_master_id = id;
      formData.value.adjustment_amount = res.data.adjustment_amount ?? 0;
      formData.value.invoice_date = date;
      formData.value.date = dateHelper.currentDate();
      tableItems.push(...formatGeneralItems(general));

      if (receipt_generals && receipt_generals[0]) {
        formData.value.money_receipt_no = receipt_generals[0].receipt_master.voucher_no;
        formData.value.mop_account_head_id = receipt_generals[0].receipt_master.account_head_id;
        formData.value.receipt_master_id = receipt_generals[0].receipt_master.id;
        formData.value.receipt_reference = receipt_generals[0].receipt_master.receipt_reference;
        formData.value.total_return_amount = res.data.paid_amount;
      }
      if (discount_ledger) {
        formData.value.discount_head_id = discount_ledger.account_head_id;
        formData.value.adjustment_amount = discount_ledger.debit;
      }
    })
  ])
    .then(() => {
      loader.value = false;
    })
    .catch(() => {
      loader.value = false;
    });
});

const goToList = () => {
  const {redirect_route, ...routeQuery} = $route.query;
  const routeName = redirect_route ? redirect_route : "bill-manager";
  $router.push({
    name: routeName,
    params: $route.params,
    query: {
      ...routeQuery
    }
  });
};

const formatReturnItem = (item) => {
  return {
    product_id: item.product_id,
    quantity: item.return_qty,
    rate: item.rate,
    vat: item.vat,
    discount_amount: item.discount_amount,
    discount_percent: item.discount_percent,
    vat_amount: item.vat_amount,
    amount: item.return_amount,
    total_amount: item.total_amount,
    sale_general_id: item.id,
    stock_in_hand: item.stock_in_hand,
    prev_return_qty: item.prev_return_qty,
    description: item.description,
  };
};

const handleSubmit = () => {
  loader.value = true;

  formData.value.item_details = tableItems;
  let copyFormData = Object.assign({}, formData.value);
  let isAllItemsReturned = !formData.value.item_details.find(item => parseInt(item.return_qty ?? '0') !== (item.quantity - item.prev_return_qty));

  copyFormData.item_details = formData.value.item_details.map(item => formatReturnItem(item));
  copyFormData.sale_type = $route.query.sale_type ?? "ipd_pharmacy_invoice";
  copyFormData.receipt_type = "ipd_pharmacy_invoice_return";
  copyFormData.return_type = isAllItemsReturned ? "returned" : "partially_returned";
  copyFormData.adjustment_amount = formData.value.return_roundup_discount;

  returnBillings(copyFormData).then(res => {
    if (!res.status) {
      return showError(res.message);
    }
    showSuccess(res.message);
    fetchAndGeneratePdf(company.value, res.data.id, 'ipd_pharmacy_invoice');
    goToList();
  }).finally(() => {
    loader.value = false;
  });
};

</script>
<style scoped>
address {
  margin-bottom: 0 !important;
}
</style>
